import { isPast, parseISO } from "date-fns";
import { isAppleWebView } from "utilities/display";
import { useIsLoggedIn } from "hooks";
import useCanViewPromotions from "./useCanViewPromotions";
import { usePageContext } from "./usePageContext";
import useHubs, { useHub } from "./graphql/useHubs";
import type { Card } from "store/Braze";
import { useIsClient } from "usehooks-ts";

export const useContentfulBanners = (): Card[] => {
  const { hub: hubSlug, locale } = usePageContext();
  const hub = useHub(hubSlug);
  const hubs = useHubs();

  const allBanners = hub?.banners || hubs[0]?.banners || [];
  const isLoggedIn = useIsLoggedIn();
  const isClient = useIsClient();
  const canViewPromotions = useCanViewPromotions();

  if (!isClient) {
    return [];
  }

  return allBanners
    .filter(({ restrict, regions, isPromotion, expiresAt, showFrom }) => {
      const canView =
        restrict === "users"
          ? isLoggedIn
          : restrict === "guests"
            ? !isLoggedIn
            : true;

      const shouldShow = showFrom ? isPast(parseISO(showFrom)) : true;
      const hasExpired = expiresAt && isPast(parseISO(expiresAt));
      const hasPermission = isPromotion !== true || canViewPromotions;
      const matchesLocale = (Array.isArray(regions) ? regions : []).some(
        (region) => {
          return (
            (isAppleWebView() ? "en-au" : locale.toLowerCase()) ===
            `${region.languages[0]}-${region.code}`
          );
        },
      );

      return (
        canView && matchesLocale && hasPermission && !hasExpired && shouldShow
      );
    })
    .map((banner) => ({
      ...banner,
      imageUrl: banner.mediaMobile.file.url,
      url: banner.href,
      linkText: banner.title,
      extras: {
        desktopImageUrl: banner.media.file.url,
      },
    }));
};
